import { useContext, useEffect, useMemo, useRef } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { getAllowedTargets } from '../kanban/kanbanTransitionPolicy';
import { notifyRefusal, classifyRefusal } from '../kanban/kanbanNotify';

const DANGER_STATES = ['cancelled', 'blocked'];

/**
 * TaskStatusSelector — workflow-aware transition selector.
 *
 * Supports two modes depending on the props provided:
 *
 * **Workflow mode** (now ACTIVE for every workflow-driven type): when a
 * `props.workflow` (the type's { workflowStates, workflowTransitions }) and a
 * `props.task` are provided, the selectable transitions are NOT computed here
 * — they are DELEGATED to the single policy source (getAllowedTargets), exactly
 * like the Kanban card menu and the drag-and-drop guard. This unifies the
 * decision (reachable ∩ role-permitted ∩ subtasks-OK) so the fiche switch and
 * the board can never disagree. The current state itself is always shown (as
 * the active step) even though it is not a transition target.
 *
 * The component requires the policy context to be seeded by the parent
 * (TaskEdit calls setPolicyContext with taskTypes/userRoles/allTasks). When no
 * workflow is provided, it falls back to **legacy mode** (flat taxonomy
 * `props.options`) — for task types that are not yet workflow-managed.
 */
export const TaskStatusSelector = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const { formData: currentState, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    // Workflow of the task's TYPE: { workflowStates, workflowTransitions }.
    // Presence of a transitions map is what switches the component into
    // workflow mode (vs. the legacy flat-taxonomy fallback).
    const workflow = evaluateTemplateValue({
        valueToEvaluate: props.workflow,
        globalDataContext,
        templateContext,
    });

    // The focused task in policy shape ({ data: { id, type, state, parentId } }).
    // Passed straight to getAllowedTargets so the same gate the board uses also
    // gates this switch. Falls back to a minimal shape built from currentState
    // + props.taskType when the parent only wired scalars.
    const taskObj = evaluateTemplateValue({
        valueToEvaluate: props.task,
        globalDataContext,
        templateContext,
    });
    const taskTypeId = evaluateTemplateValue({
        valueToEvaluate: props.taskType,
        globalDataContext,
        templateContext,
    });

    // Legacy options (taxonomy-based) — kept for backward compat.
    const legacyOptions = evaluateTemplateValue({
        valueToEvaluate: props.options,
        globalDataContext,
        templateContext,
    }) || [];

    // Point 1 on the task sheet (parity with the board): surface WHY a status
    // change was refused by the server (e.g. the T28 subtask guard, or a role
    // permission). DataSync writes its outcome to ~~.taskSyncable.status
    // ({ type: 'info'|'success'|'error', message }); on an 'error' we raise the
    // SAME motivated toast the board uses (kanbanNotify bus + shared
    // classifyRefusal). A ref de-dupes so one failure notifies exactly once.
    const saveStatus = evaluateTemplateValue({
        valueToEvaluate: props.saveStatus,
        globalDataContext,
        templateContext,
    });
    const lastNotifiedRef = useRef('');
    useEffect(() => {
        if (saveStatus?.type === 'error' && saveStatus?.message) {
            if (saveStatus.message !== lastNotifiedRef.current) {
                lastNotifiedRef.current = saveStatus.message;
                notifyRefusal(classifyRefusal(saveStatus.message));
            }
        } else if (saveStatus?.type && saveStatus.type !== 'error') {
            lastNotifiedRef.current = '';
        }
    }, [saveStatus?.type, saveStatus?.message]);

    const handleSelect = (value) => {
        globalDataContext.updateData(value, formDataPath);
    };

    // ── Workflow mode ────────────────────────────────────────────────────────
    // Build the visible step list by DELEGATING the allow-decision to the policy
    // (getAllowedTargets) and prepending the current state so it renders as the
    // active step. We never re-implement the role / subtask gate here.
    const workflowStates = workflow?.workflowStates ?? workflow?.states ?? null;
    const workflowOptions = useMemo(() => {
        if (!workflowStates) return null;

        const states = workflowStates;
        // Build the policy task, but ALWAYS reconcile the LIVE current state (the
        // dataLocation value, which the user's pick mutates immediately) over any
        // stale state carried by the passed task object — otherwise the reachable
        // list would freeze on the original state until a save+reload. Keep the
        // task's id/type (needed by the policy's subtask-closure guard).
        const base = (taskObj && typeof taskObj === 'object') ? taskObj : { data: {} };
        const baseData = base.data || base;
        const policyTask = {
            data: {
                ...baseData,
                type: baseData.type ?? taskTypeId,
                state: currentState || baseData.state,
            },
        };

        const targets = getAllowedTargets(policyTask); // [{ to, label }]

        // Always include the current state as the (active) anchor step.
        const current = currentState
            ? [{ value: currentState, label: states[currentState]?.label ?? currentState }]
            : [];
        const reachable = targets.map(t => ({
            value: t.to,
            label: t.label ?? states[t.to]?.label ?? t.to,
        }));

        // De-duplicate (current may also be a target of a self-loop transition).
        const seen = new Set();
        return [...current, ...reachable].filter(o => {
            if (seen.has(o.value)) return false;
            seen.add(o.value);
            return true;
        });
    }, [workflowStates, taskObj, taskTypeId, currentState]);

    // ── Render ───────────────────────────────────────────────────────────────

    if (workflowOptions !== null) {
        const dangerOptions = workflowOptions.filter(o =>
            DANGER_STATES.some(d => o.value.toLowerCase().includes(d))
        );
        const mainOptions = workflowOptions.filter(o =>
            !DANGER_STATES.some(d => o.value.toLowerCase().includes(d))
        );

        const isActive = (opt) => opt.value === currentState;

        const dangerBtnClass = (opt) => [
            'px-3 py-1.5 text-xs font-bold transition-all flex-shrink-0 rounded-md border',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-iris-500',
            isActive(opt)
                ? 'bg-rose-600 text-white border-rose-700 shadow-md'
                : 'bg-white text-slate-500 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200',
        ].join(' ');

        const mainBtnClass = (opt, idx, arr) => {
            const active = isActive(opt);
            const currentIdx = arr.findIndex(o => o.value === currentState);
            const isCompleted = currentIdx !== -1 && idx < currentIdx;

            return [
                'relative h-8 px-4 text-xs font-bold transition-all flex items-center justify-center flex-shrink-0 min-w-[100px]',
                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-iris-500 rounded-sm',
                idx !== 0 ? '-ml-3 pl-6' : '',
                idx !== arr.length - 1 ? 'pr-6' : '',
                active
                    ? 'bg-iris-600 text-white shadow-md'
                    : isCompleted
                        ? 'bg-iris-100 text-iris-700 hover:bg-iris-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
            ].filter(Boolean).join(' ');
        };

        const mainBtnStyle = (idx, arr) => {
            if (idx === arr.length - 1) {
                return { zIndex: arr.length - idx, clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' };
            }
            return {
                zIndex: arr.length - idx,
                filter: 'drop-shadow(1px 0px 0px white)',
                clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
            };
        };

        // Only the current state, no reachable target → nothing to switch to.
        if (workflowOptions.length <= 1) {
            const currentStateLabel = workflowStates?.[currentState]?.label ?? currentState;
            return (
                <ActionDependant {...props}>
                    <div className="flex items-center py-2">
                        <span className="px-3 py-1.5 text-xs font-bold bg-iris-600 text-white rounded-md">
                            {currentStateLabel}
                        </span>
                        <span className="ml-3 text-xs text-slate-400 italic">Aucune transition disponible</span>
                    </div>
                </ActionDependant>
            );
        }

        return (
            <ActionDependant {...props}>
                <div className="flex items-center overflow-x-auto py-2 gap-4" role="group" aria-label="Statut de la tâche">
                    {dangerOptions.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelect(opt.value)}
                            className={dangerBtnClass(opt)}
                            aria-current={isActive(opt) ? 'step' : undefined}
                        >
                            {isActive(opt) && <span className="sr-only">État actuel : </span>}
                            {opt.label}
                        </button>
                    ))}
                    <div className="flex items-center">
                        {mainOptions.map((opt, idx, arr) => {
                            const active = isActive(opt);
                            const currentIdx = arr.findIndex(o => o.value === currentState);
                            const completed = currentIdx !== -1 && idx < currentIdx;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleSelect(opt.value)}
                                    style={mainBtnStyle(idx, arr)}
                                    className={mainBtnClass(opt, idx, arr)}
                                    aria-current={active ? 'step' : undefined}
                                >
                                    {/* Non-colour cue (WCAG 1.4.1): ✓ = franchie, ● = étape
                                        actuelle ; libellé sr-only pour le lecteur d'écran. */}
                                    {active && <span className="sr-only">État actuel : </span>}
                                    {completed && <span aria-hidden="true">✓ </span>}
                                    {active && <span aria-hidden="true">● </span>}
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </ActionDependant>
        );
    }

    // ── Legacy mode (taxonomy options) ───────────────────────────────────────
    const DANGER_LABELS = ['Annulé', 'Bloqué'];
    const mainOptions = legacyOptions.filter(o => !DANGER_LABELS.includes(o.label));
    const cancelOption = legacyOptions.find(o => o.label === 'Annulé');
    const blockedOption = legacyOptions.find(o => o.label === 'Bloqué');

    const isActive = (opt) => opt.value === currentState;

    const dangerBtnClass = (opt) => [
        'px-3 py-1.5 text-xs font-bold transition-all flex-shrink-0 rounded-md border',
        isActive(opt)
            ? 'bg-rose-600 text-white border-rose-700 shadow-md'
            : 'bg-white text-slate-500 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200',
    ].join(' ');

    const mainBtnClass = (opt, idx, arr) => {
        const active = isActive(opt);
        const currentIdx = arr.findIndex(o => o.value === currentState);
        const isCompleted = currentIdx !== -1 && idx < currentIdx;

        return [
            'relative h-8 px-4 text-xs font-bold transition-all flex items-center justify-center flex-shrink-0 min-w-[100px]',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-iris-500 rounded-sm',
            idx !== 0 ? '-ml-3 pl-6' : '',
            idx !== arr.length - 1 ? 'pr-6' : '',
            active
                ? 'bg-iris-600 text-white shadow-md'
                : isCompleted
                    ? 'bg-iris-100 text-iris-700 hover:bg-iris-200'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
        ].filter(Boolean).join(' ');
    };

    const mainBtnStyle = (idx, arr) => {
        if (idx === arr.length - 1) {
            return { zIndex: arr.length - idx, clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' };
        }
        return {
            zIndex: arr.length - idx,
            filter: 'drop-shadow(1px 0px 0px white)',
            clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
        };
    };

    return (
        <ActionDependant {...props}>
            <div className="flex items-center overflow-x-auto py-2 gap-4">
                {cancelOption && (
                    <button
                        type="button"
                        onClick={() => handleSelect(cancelOption.value)}
                        className={dangerBtnClass(cancelOption)}
                    >
                        {cancelOption.label}
                    </button>
                )}
                <div className="flex items-center">
                    {mainOptions.map((opt, idx, arr) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelect(opt.value)}
                            style={mainBtnStyle(idx, arr)}
                            className={mainBtnClass(opt, idx, arr)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {blockedOption && (
                    <button
                        type="button"
                        onClick={() => handleSelect(blockedOption.value)}
                        className={dangerBtnClass(blockedOption)}
                    >
                        {blockedOption.label}
                    </button>
                )}
            </div>
        </ActionDependant>
    );
};
